import React, { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Button, Spinner, Container, Row, Col } from 'react-bootstrap';
import DateFilterInput from '../Utils/DateFilterInput';
import { convertDateToISO, getCurrentDate, getCurrentDateMinusDays } from '../Utils/utils';
import { saldosReportPdf, saldosReportCsv } from '../../services/Reports/reportsService';
import PersonInput from '../Utils/PersonInput';
import ProductInput from '../Utils/ProductInput';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Saldos = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCsv, setIsLoadingCsv] = useState(false);
  const { handleSubmit, formState: {}, control, watch, setValue, getValues, trigger } = useForm({});
  const selectedPerson = watch('person');
  const datesRequired = !selectedPerson?.id;

  // Recalcula permissões sempre que o objeto permissions mudar
  const canView = useMemo(() => hasPermission('reportsView'), [hasPermission, permissions]);

  const formatPayload = (data) => {
    const getDateOrNull = (dateStr) => {
      if (!dateStr || String(dateStr).trim() === '') {
        return null;
      }
      const isValid = /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr);
      return isValid ? convertDateToISO(dateStr) : null;
    };

    const defaultInitialDate = getCurrentDateMinusDays(30);
    const defaultFinalDate = getCurrentDate();
    const hasPerson = !!data?.person?.id;

    return {
      initialDate: hasPerson
        ? getDateOrNull(data?.initialDate)
        : getDateOrNull(data?.initialDate) ?? defaultInitialDate,
      finalDate: hasPerson
        ? getDateOrNull(data?.finalDate)
        : getDateOrNull(data?.finalDate) ?? defaultFinalDate,
      person: data.person?.id || null,
      product: data.product?.id || null
    };
  };

  const onSubmit = async (data) => {
    if (!checkPermission('reportsView')) {
      return;
    }
    setIsLoading(true);
    try {
      const formattedPayload = formatPayload(data);
      await saldosReportPdf(formattedPayload);
    } 
    catch (error) {
      console.log(error);
    } 
    finally {
      setIsLoading(false);
    }
  };

  const onExportCsv = async () => {
    if (!checkPermission('reportsView')) {
      return;
    }
    
    if (!getValues('person')?.id) {
      const isValid = await trigger(['initialDate', 'finalDate']);
      if (!isValid) {
        return;
      }
    }
    
    setIsLoadingCsv(true);
    try {
      const formData = getValues();
      const formattedPayload = formatPayload(formData);
      await saldosReportCsv(formattedPayload);
    } 
    catch (error) {
      console.log(error);
    } 
    finally {
      setIsLoadingCsv(false);
    }
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Relatório de Saldos</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Relatório de Saldos</h3>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3">
          <Row>
            <Col md={3}>
              <Controller
                name="initialDate"
                control={control}
                rules={{
                  required: datesRequired ? 'A data inicial é obrigatória.' : false,
                  validate: (value) => {
                    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                    if (value && !dateRegex.test(value)) return 'Data inválida';
                    return true;
                  }
                }}
                render={({ field, fieldState }) => (
                  <DateFilterInput
                    label="Data Inicial"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error}
                    required={datesRequired}
                  />
                )}
              />
            </Col>
            <Col md={3}>
              <Controller
                name="finalDate"
                control={control}
                rules={{
                  required: datesRequired ? 'A data final é obrigatória.' : false,
                  validate: (value) => {
                    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                    if (value && !dateRegex.test(value)) return 'Data inválida';
                    return true;
                  }
                }}
                render={({ field, fieldState }) => (
                  <DateFilterInput
                    label="Data Final"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error}
                    required={datesRequired}
                  />
                )}
              />
            </Col>
            <Col md={3}>
              <Controller
                name="person"
                control={control}
                render={({ field, fieldState }) => (
                  <PersonInput
                    value={field.value}
                    createdNew={false}
                    onChange={(selected) => setValue('person', selected)}
                    onCreated={(created) => {
                      setValue('person', { id: created.id, name: created.name });
                    }}
                    label="Cliente"
                    isInvalid={!!fieldState.error}
                    type={['isCustomer']}
                  />
                )}
              />
            </Col>
            <Col md={3}>
              <Controller
                name="product"
                control={control}
                render={({ field, fieldState }) => (
                  <ProductInput
                    value={field.value}
                    createdNew={false}
                    onChange={(selected) => {
                      setValue('product', selected);
                    }}
                    onCreated={(created) => {
                      setValue('product', { id: created.id, name: created.name });
                    }}
                    label="Produto / Cultura"
                    isInvalid={!!fieldState.error}
                  />
                )}
              />
            </Col>
          </Row>
        </Form.Group>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            className="button-custom"
            type="submit"
            disabled={isLoading || isLoadingCsv}
          >
            {isLoading ? (
              <>
                Aguarde <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              </>
            ) : (
              'Imprimir PDF'
            )}
          </Button>
          <Button
            variant="success"
            className="button-custom"
            type="button"
            onClick={onExportCsv}
            disabled={isLoading || isLoadingCsv}
          >
            {isLoadingCsv ? (
              <>
                Aguarde <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              </>
            ) : (
              'Exportar CSV'
            )}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default Saldos;

